import {FC, Fragment} from 'react';
import {PricePreviewProps} from 'common/types';
import {useMoment} from 'common/useMoment';
import {BASE_PRICE_PEOPLE} from 'common/constants';
import {formatPrice} from 'common/formatPrice';
import styled from 'styled-components';

const Table = styled.table`
  padding: ${({theme}) => theme.spacing(0.75)}px;
  width: 100%;
  border-radius: 5px;
  border: 1px solid ${({theme}) => theme.colors.blue};
  transition: border 250ms ease-in-out;
  box-shadow: none;
`;

const TBody = styled.tbody``;

const TRow = styled.tr`
  padding: 0;
  margin: 0;
`;

const TCol = styled.td`
  padding: 0;
  margin: 0;
`;

const PriceCol = styled.td`
  min-width: 100px;
  vertical-align: top;
  padding: 0 0 0 ${({theme}) => theme.spacing(0.5)}px;
  margin: 0;
  text-align: right;
`;

const Text = styled.span<{bold?: boolean; primary?: boolean}>`
  font-weight: ${({bold}) => (bold ? 700 : 500)};
  ${({primary, theme}) => primary && `color: ${theme.colors.blue}`};
  font-size: ${({theme}) => theme.fontSize(0.75)}px;
  line-height: ${({theme}) => theme.fontSize(0.75)}px;
`;

const Spacer = styled.div`
  margin: ${({theme}) => theme.spacing(0.35)}px 0;
`;

const HR = styled.div`
  width: 100%;
  border-bottom: 1px solid ${({theme}) => theme.colors.lightgray};
`;

const PricePreview: FC<PricePreviewProps> = props => {
  const moment = useMoment();
  const nightsNumber = moment(props.to).diff(moment(props.from), 'days');
  const nights = nightsNumber === 1 ? `1 Nächtigung` : `${nightsNumber} Nächtigungen`;

  return (
    <Table cellSpacing={0}>
      <TBody>
        {props.price.items.map((item, index) => {
          const basePrice = item.base;
          const additionalPeople = props.price.people > BASE_PRICE_PEOPLE;
          const additionalPriceItem = item.additional;
          const additionalPriceTotal = item.additional * (props.price.people - BASE_PRICE_PEOPLE);
          const pricePerNight = basePrice + (additionalPeople ? additionalPriceTotal : 0);
          const from = moment.utc(item.from).format('D. MMM YYYY');
          const to = moment.utc(item.to).format('D. MMM YYYY');

          return (
            <Fragment key={index}>
              {props.price.items.length > 1 && (
                <>
                  <TRow>
                    <TCol colSpan={2}>
                      <Text primary>
                        {from} bis {to}
                      </Text>
                    </TCol>
                  </TRow>
                  <TRow>
                    <TCol colSpan={2}>
                      <Spacer />
                    </TCol>
                  </TRow>
                </>
              )}
              <TRow>
                <TCol>
                  <Text>Grundpreis für eine Nacht</Text>
                  {additionalPeople && <Text> (bis zu 4 Personen)</Text>}
                </TCol>
                <PriceCol>
                  <Text>{formatPrice(basePrice)}</Text>
                </PriceCol>
              </TRow>
              {additionalPeople && (
                <>
                  <TRow>
                    <TCol>
                      <Text>pro weitere Person {formatPrice(additionalPriceItem)}</Text>
                    </TCol>
                    <PriceCol>
                      <Text>{formatPrice(additionalPriceTotal)}</Text>
                    </PriceCol>
                  </TRow>
                  <TRow>
                    <TCol>
                      <Text>Preis pro Nacht</Text>
                    </TCol>
                    <PriceCol>
                      <Text bold>{formatPrice(pricePerNight)}</Text>
                    </PriceCol>
                  </TRow>
                </>
              )}
              <TRow>
                <TCol colSpan={2}>
                  <Spacer>
                    <HR />
                  </Spacer>
                </TCol>
              </TRow>
            </Fragment>
          );
        })}
        <TRow>
          <TCol>
            <Text bold>einmalige Endreinigung</Text>
          </TCol>
          <PriceCol>
            <Text bold>{formatPrice(props.price.cleaning)}</Text>
          </PriceCol>
        </TRow>
        <TRow>
          <TCol colSpan={2}>
            <Spacer>
              <HR />
            </Spacer>
          </TCol>
        </TRow>
        <TRow>
          <TCol>
            <Text bold>Gesamtpreis für {nights}</Text>
          </TCol>
          <PriceCol>
            <Text bold>{formatPrice(props.price.total)}</Text>
          </PriceCol>
        </TRow>
        <TRow>
          <TCol colSpan={2}>
            <br />
            <Text>Die berechneten Preisangaben sind unverbindlich und exklusive Ortstaxe.</Text>
          </TCol>
        </TRow>
      </TBody>
    </Table>
  );
};

export default PricePreview;
